import type { StatsigEvent } from "@statsig/js-client";
import { isOnScreen } from "./hooks/use-on-visible";

/**
 * One latch per document: has this page ever actually been on screen?
 *
 * Owlet warm-loads Felt pages at app launch, so a page can mount — and run
 * every mount effect on it — minutes before the parent sees it, or entirely
 * without them seeing it. Events emitted in that window were billed against
 * the Statsig contract for screens nobody looked at (PE-1775).
 *
 * This is deliberately a **latch**, not a live `isOnScreen()` check at send
 * time. Once the parent has seen the page, everything flows, including events
 * fired later while the app is backgrounded — a dismissal or an unload on a
 * page they genuinely viewed is real, and a live check would silently eat it.
 * The only traffic suppressed is from a page that has never once been visible.
 */
let hasBeenVisible = false;

type Sender = (event: StatsigEvent) => void;

interface HeldEvent {
  event: StatsigEvent;
  send: Sender;
  heldSince: number;
}

/**
 * Events emitted before the page was first seen. Bounded: a page parked for a
 * long time with a chatty mount path must not grow this without limit. Once
 * full we stop accepting — the overflow is reported once, not per event, so one
 * parked page is one Sentry breadcrumb rather than a flood.
 */
const held: HeldEvent[] = [];
const MAX_HELD = 100;
let overflowed = false;

/** Reported once per document when the hold buffer fills. */
export interface VisibilityGateOverflow {
  message: string;
  droppedAfter: number;
}

let onOverflow: ((overflow: VisibilityGateOverflow) => void) | undefined;

/** Wire a reporter (Sentry) for hold-buffer overflow. Optional. */
export function setVisibilityGateOverflowReporter(
  reporter: (overflow: VisibilityGateOverflow) => void
): void {
  onOverflow = reporter;
}

/**
 * Open the latch and flush anything held. Idempotent — safe to call on every
 * visible tick.
 */
export function markPageVisible(): void {
  if (hasBeenVisible) return;
  hasBeenVisible = true;

  const now = Date.now();
  const queued = held.splice(0, held.length);
  for (const { event, send, heldSince } of queued) {
    // Statsig's public StatsigEvent has no `time` field — it stamps events at
    // enqueue — so a held event necessarily carries its flush time, not its
    // emit time. Record the gap so the skew is visible in the data rather than
    // silently baked into it.
    send({
      ...event,
      metadata: { ...event.metadata, held_ms: String(now - heldSince) },
    });
  }
}

/**
 * Send now if the page has ever been seen; otherwise hold until it is.
 *
 * Holding rather than dropping matters: a warm-loaded page that the parent
 * later opens has already run its mount effects, so dropping would silently
 * under-count every real view that arrived via a warm load.
 */
export function sendWhenVisible(event: StatsigEvent, send: Sender): void {
  // Self-heal: if the page is on screen right now, the latch should already be
  // open. This keeps correctness independent of anything having mounted a
  // visibility watcher — the watcher only speeds up the hidden→visible case.
  if (!hasBeenVisible && isOnScreen()) markPageVisible();

  if (hasBeenVisible) {
    send(event);
    return;
  }

  if (held.length >= MAX_HELD) {
    if (!overflowed) {
      overflowed = true;
      onOverflow?.({
        message:
          "Statsig visibility gate: hold buffer full on a page that has never been visible; further events dropped",
        droppedAfter: MAX_HELD,
      });
    }
    return;
  }

  held.push({ event, send, heldSince: Date.now() });
}

/** Test seam — resets the per-document latch. */
export function __resetVisibilityGate(): void {
  hasBeenVisible = false;
  held.length = 0;
  overflowed = false;
  onOverflow = undefined;
}
